import type { Repository } from '../../../types';

export type RepoState =
  | 'connected'
  | 'disconnected'
  | 'connectable'
  | 'unimported'
  | 'empty';

/**
 * Returns true if the repo has no default branch or branch list, meaning
 * there is nothing meaningful to import or display.
 */
export function isEmptyRepo(repo: Pick<Repository, 'branch' | 'branches'>): boolean {
  return !repo.branch || !(repo.branches?.length);
}

/**
 * Classify a repository's relationship to the active GitHub App installation.
 * Note: pre-hybrid imports can leave a repo both connected (QI) and connectable
 * (CLI) for the same locator, so "disconnected" specifically requires
 * connectable && !connected on a `github-app` project.
 */
export function classifyRepo(repo: Repository): RepoState {
  // Connected projects are repos from GitHub whose "owner/repo" match an existing FOSSA project locator.
  // They also have a vcs_host value of "github-app" and a correponding GitHubAppInstallationProject row.
  const isConnected = !!repo.connectedProjects?.length;

  // Connectable repos are repos from GitHub whose "owner/repo" match an existing FOSSA project locator,
  // but do NOT have a vcs_host value of "github-app" or a corresponding GitHubAppInstallationProject row.
  const isConnectable = !!repo.connectableProjects?.length;

  // Disconnected projects are Quick Import projects (type === 'autobuild')
  // that are no longer connected to the GitHub App (see definition of "connected" above).
  const isQuickImportProject = repo.project?.type === 'autobuild';

  // Only projects originally imported via the GitHub App should be considered "disconnected".
  // We reset the "Project.vcs_host" to null when disconnecting so both null and "github-app"
  // are valid indicators of a previously connected GitHub App project.
  const isEligibleForGitHubAppConnection = repo.project?.vcs_host === null || repo.project?.vcs_host === 'github-app';

  if (isConnectable && !isConnected && isQuickImportProject && isEligibleForGitHubAppConnection) {
    return 'disconnected';
  }

  // This case covers projects that have already been imported via GitHub OAuth
  // (or any other non GitHub App QI integration). Unless we want to push users toward
  // switching integrations to the GitHub App, these should be considered "connected".
  if (isConnectable && isQuickImportProject && !isEligibleForGitHubAppConnection) {
    return 'connected';
  }

  if (isConnected) {
    return 'connected';
  }

  if (isConnectable) {
    return 'connectable';
  }

  if (isEmptyRepo(repo)) {
    return 'empty';
  }

  return 'unimported';
}
